import React from 'react';
import { Feature, MainMenu } from '@tencent/workbench';
import BaseDrawerFeature from '@framework/workbench/common/feature/base-drawer-feature';
import { ActionType } from '@src/framework/editor/src/js/constants/common-action-type';
import { FormItemName, INPUT_PROPS_CALLBACK_KEY } from '@framework/workbench/drawer/constants';
import FormItemDropdown from '@src/framework/workbench/drawer/components/dropdown';
import { PICTURE_TAILOR } from '@framework/workbench/drawer/image';
import i18n from '@i18next/i18n';
import { IPictureModel } from '@src/slide/domain/ui-model/element-modeles/Interface/picture-model.interface';
import { convertCmToPx, convertPxToCm } from '@src/slide/infrastructure/module/independent/unit';
import { IPictureCropFeature } from './picture-crop-interface';
import { NumberInputFrom } from '@tencent/wb-ext-form';
import { transaction } from '@src/slide/domain/slide-app';
import { MODES } from '@src/slide/domain/ui-model/element-modeles/property/frame/frame.interface';
import { afterRotate } from '@src/slide/infrastructure/module/independent/graphic';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { IForegroundSize } from '@src/slide/domain/ui-model/element-modeles/property/image-size/image-size.interface';
import { handleInputBlurInitAndReset } from '@src/slide/application/service/drawer-input-manager/drawer-handler';

const DEFAULT_VALUE = 0;
const HAS_FLIP = 1; // 是否有翻转
const FILL_RECT_SUM = -1;   // 当充满的上下左右偏移值相加为-1时，需要考虑图片位置是否置灰

interface IOffsetInfo {
    offsetX: number,
    offsetY: number,
}

@Feature()
export class PictureCropFeature extends BaseDrawerFeature implements IPictureCropFeature {
    public renderTailor() {
        return (
            <FormItemDropdown
                icon={PICTURE_TAILOR}
                dropContent={this.getContent()}
                onClick={this.handleClick}
                text={i18n.t('裁剪')}
                paragraphIconStyle={{ borderRight: '1px solid rgba(0, 0, 0, 0.12)', padding: '4px 0' }}
                dropDownIconStyle={{ width: 28 }}
                btnDisable={this.isCropBtnDisable()}
                onDropdownVisibleChange={this.onDropdownVisibleChange}
            />
        );
    }

    public getPictureHeight(): number {
        const height = this.getImageSizeDetail()?.rh ?? DEFAULT_VALUE;
        return convertPxToCm(height);
    }

    public getPictureWidth(): number {
        const width = this.getImageSizeDetail()?.rw ?? DEFAULT_VALUE;
        return convertPxToCm(width);
    }

    public getPictureOffsetX(): number {
        const { offsetX } = this.getOffset();
        return convertPxToCm(offsetX);
    }

    public getPictureOffsetY(): number {
        const { offsetY } = this.getOffset();
        return convertPxToCm(offsetY);
    }

    public getCropHeight(): number {
        const height = this.getFrame()?.getHeight() ?? DEFAULT_VALUE;
        return convertPxToCm(height);
    }

    public getCropWidth(): number {
        const width = this.getFrame()?.getWidth() ?? DEFAULT_VALUE;
        return convertPxToCm(width);
    }

    public getCropLeft(): number {
        const left = this.getFrame()?.getLeft(true) ?? DEFAULT_VALUE;
        return convertPxToCm(left);
    }

    public getCropTop(): number {
        const top = this.getFrame()?.getTop(true) ?? DEFAULT_VALUE;
        return convertPxToCm(top);
    }

    @handleInputBlurInitAndReset(INPUT_PROPS_CALLBACK_KEY.PICTURE_POSITIOIN)
    public handlePictureWidth(width: number, from: NumberInputFrom): void {
        if (!this.hasImageSize() || this.isInputOnlyChangeUI()) return;
        const { rw, pl } = this.getImageSizeDetail();
        const widthPx = convertCmToPx(width);
        const newPl = ((widthPx - rw) / 2) + pl;
        this.updateImageSizeDetail({ rw: widthPx, pl: newPl });
    }

    @handleInputBlurInitAndReset(INPUT_PROPS_CALLBACK_KEY.PICTURE_POSITIOIN)
    public handlePictureHeight(height: number, from: NumberInputFrom): void {
        if (!this.hasImageSize() || this.isInputOnlyChangeUI()) return;
        const { rh, pt } = this.getImageSizeDetail();
        const heightPx = convertCmToPx(height);
        const newPt = ((heightPx - rh) / 2) + pt;
        this.updateImageSizeDetail({ rh: heightPx, pt: newPt });
    }

    @handleInputBlurInitAndReset(INPUT_PROPS_CALLBACK_KEY.PICTURE_POSITIOIN)
    public handlePictureOffsetX(offsetX: number, from: NumberInputFrom): void {
        if (!this.hasImageSize() || this.isInputOnlyChangeUI()) return;
        const { pl } = this.getImageSizeDetail();
        const newPl = pl - convertCmToPx(offsetX - this.getPictureOffsetX());
        this.updateImageSizeDetail({ pl: newPl });
    }

    @handleInputBlurInitAndReset(INPUT_PROPS_CALLBACK_KEY.PICTURE_POSITIOIN)
    public handlePictureOffsetY(offsetY: number, from: NumberInputFrom): void {
        if (!this.hasImageSize() || this.isInputOnlyChangeUI()) return;
        const { pt } = this.getImageSizeDetail();
        const newPt = pt - convertCmToPx(offsetY - this.getPictureOffsetY());
        this.updateImageSizeDetail({ pt: newPt });
    }

    @handleInputBlurInitAndReset(INPUT_PROPS_CALLBACK_KEY.PICTURE_POSITIOIN)
    public handleCropWidth(width: number, from: NumberInputFrom): void {
        if (!this.hasImageSize() || this.isInputOnlyChangeUI()) return;
        const { pr } = this.getImageSizeDetail();
        const newPr = pr - convertCmToPx((width - this.getCropWidth()));
        // 图片翻转后，修改宽度时mode为LEFT，否则为RIGHT
        const { flipH } = (this.getSelectModel() as IPictureModel).props.style;
        const mode = flipH === HAS_FLIP ? MODES.LEFT : MODES.RIGHT;
        transaction(() => {
            const newWidthPx = convertCmToPx(width);
            this.getFrame().updateWidth({ value: newWidthPx, isIgnoreLock: true, mode });
            // 当图片未裁剪并且图片宽度变为0的情况下，不改变pr
            if (width !== DEFAULT_VALUE || this.hasCrop()) {
                this.updateImageSizeDetail({ pr: newPr }, newWidthPx);
            }
        });
    }

    @handleInputBlurInitAndReset(INPUT_PROPS_CALLBACK_KEY.PICTURE_POSITIOIN)
    public handleCropHeight(height: number, from: NumberInputFrom): void {
        if (!this.hasImageSize() || this.isInputOnlyChangeUI()) return;
        const { pb } = this.getImageSizeDetail();
        const newPb = pb - convertCmToPx((height - this.getCropHeight()));
        // 图片翻转后，修改宽度时mode为TOP，否则为BOTTOM
        const { flipV } = (this.getSelectModel() as IPictureModel).props.style;
        const mode = flipV === HAS_FLIP ? MODES.TOP : MODES.BOTTOM;
        transaction(() => {
            const newHeightPx = convertCmToPx(height);
            this.getFrame().updateHeight({ value: newHeightPx, isIgnoreLock: true, mode });
            if (height !== DEFAULT_VALUE || this.hasCrop()) {
                this.updateImageSizeDetail({ pb: newPb }, undefined, newHeightPx);
            }
        });
    }

    @handleInputBlurInitAndReset(INPUT_PROPS_CALLBACK_KEY.PICTURE_POSITIOIN)
    public handleCropLeft(cropLeft: number, from: NumberInputFrom): void {
        if (!this.hasImageSize() || this.isInputOnlyChangeUI()) return;
        const top = this.getFrame()?.getTop(true) ?? DEFAULT_VALUE;
        const { newPl, newPt } = this.handleCropLeftOrTop(convertCmToPx(cropLeft), top);
        transaction(() => {
            this.getFrame().updateLeft(convertCmToPx(cropLeft), true);
            this.updateImageSizeDetail({ pl: newPl, pt: newPt });
        });
    }

    @handleInputBlurInitAndReset(INPUT_PROPS_CALLBACK_KEY.PICTURE_POSITIOIN)
    public handleCropTop(cropTop: number, from: NumberInputFrom): void {
        if (!this.hasImageSize() || this.isInputOnlyChangeUI()) return;
        const left = this.getFrame()?.getLeft(true) ?? DEFAULT_VALUE;
        const { newPl, newPt } = this.handleCropLeftOrTop(left, convertCmToPx(cropTop));
        transaction(() => {
            this.getFrame().updateTop(convertCmToPx(cropTop), true);
            this.updateImageSizeDetail({ pt: newPt, pl: newPl });
        });
    }

    public shouldPicturePositionDisabled = (): boolean => {
        // 形状设置图片填充时，填充方式为充满需要置灰
        if (this.getSelectModel()?.type === UIModelType.SHAPE) {
            const fillInfo = (this.getSelectModel() as IShapeModel).getBackgroundFill().getFillInfo();
            if (fillInfo?.image?.tile) {
                return true;
            }
            const fillRect = fillInfo?.image?.stretch?.fillRect;
            if (fillRect) {
                const { l = DEFAULT_VALUE, r = DEFAULT_VALUE, b = DEFAULT_VALUE, t = DEFAULT_VALUE } = fillRect;
                // 当充满的上为-1，下为0，且左+右>=-1时，图片位置不置灰，其他类似
                if (this.isValidPosition(l, r, b, t) || this.isValidPosition(b, t, l, r)) {
                    return false;
                }
                // 当充满的上+下或左+右偏移值相加<=-1时，图片位置置灰
                if (l + r <= FILL_RECT_SUM || b + t <= FILL_RECT_SUM) {
                    return true;
                }
            }
        }
        return false;
    };

    private isValidPosition = (l: number, r: number, b: number, t: number): boolean => l + r === FILL_RECT_SUM
        && (l === DEFAULT_VALUE || r === DEFAULT_VALUE) && (b + t) >= FILL_RECT_SUM;

    private getContent() {
        return <MainMenu align="left" name={FormItemName.PICTURE_TAILOR} mode="menu" itemStyle={{ width: 166 }} />;
    }

    private handleClick = () => {
        this.drawerCallbackByActionType(ActionType.TAILOR_PIC_NORMAL, 1);
    };

    private getOffset = (): IOffsetInfo => {
        const imageSize = this.getImageSizeDetail();
        if (imageSize) {
            const { pl, pt, rw, rh } = imageSize;
            const { width, height } = (this.getSelectModel() as IPictureModel).props.style;
            return {
                offsetX: ((rw - width) / 2) - pl,
                offsetY: ((rh - height) / 2) - pt,
            };
        }
        return { offsetX: DEFAULT_VALUE, offsetY: DEFAULT_VALUE };
    };

    /**
     * 处理修改裁剪位置左/上的逻辑
     * @param cropLeft 裁剪位置-left的最终值
     * @param cropTop 裁剪位置-top的最终值
     * @returns
     */
    private handleCropLeftOrTop = (cropLeft: number, cropTop: number) => {
        const { style } = (this.getSelectModel() as IPictureModel).props;
        const { left, top, width, height, flipH, flipV, rotate } = (this.getSelectModel() as IPictureModel)
            .getFrame().getFrameInfo(true) ?? style;
        const { pl, pt } = this.getImageSizeDetail();
        const diffLeft = cropLeft - convertCmToPx(this.getCropLeft());
        const diffTop = cropTop - convertCmToPx(this.getCropTop());
        let newPl = pl + (flipH === HAS_FLIP ? -diffLeft : diffLeft);
        let newPt = pt + (flipV === HAS_FLIP ? -diffTop : diffTop);
        if (rotate) {
            const { pl, pt } = this.getImageSizeDetail();
            const imageForegroundPoint = {
                x: flipH === HAS_FLIP ? left + width + pl : left - pl,
                y: flipV === HAS_FLIP ? top + height + pt : top - pt,
            }; // 前景图的位置坐标
            const imageCenterX = left + (width / 2);
            const imageCenterY = top + (height / 2);
            const imageForegroundRotatePoint = afterRotate(imageForegroundPoint, rotate, imageCenterX, imageCenterY);
            const newImageCenterX = cropLeft + (width / 2);  // 图片left变动后新的中心点坐标
            const newImageCenterY = cropTop + (height / 2);
            const newImageForegroundPoint = afterRotate(
                imageForegroundRotatePoint, -rotate,
                newImageCenterX, newImageCenterY,
            );
            newPl = flipH === HAS_FLIP
                ? newImageForegroundPoint.x - (cropLeft + width)
                : cropLeft - newImageForegroundPoint.x;
            newPt = flipV === HAS_FLIP
                ? newImageForegroundPoint.y - (cropTop + height)
                : cropTop - newImageForegroundPoint.y;
        }
        return { newPl, newPt };
    };

    private getFrame = () => (this.getSelectModel() as IPictureModel)?.getFrame?.();
    private hasImageSize = (): boolean => {
        if (this.getSelectModel()?.type === UIModelType.IMAGE) {
            return !!((this.getSelectModel() as IPictureModel)?.getImageSize?.());
        }
        return !!((this.getSelectModel() as IShapeModel)?.getImageFillSize?.());
    };

    private getImageSizeDetail = () => {
        if (this.getSelectModel()?.type === UIModelType.IMAGE) {
            return (this.getSelectModel() as IPictureModel)?.getImageSize?.().getForegroundImageSize();
        }
        return (this.getSelectModel() as IShapeModel)?.getImageFillSize?.().getBackgroundImageSize?.();
    };

    private updateImageSizeDetail = (props: Partial<IForegroundSize>, width?: number, height?: number) => {
        if (this.getSelectModel()?.type === UIModelType.IMAGE) {
            return (this.getSelectModel() as IPictureModel)?.getImageSize?.().updateForegroundImageSize(props);
        }
        return (this.getSelectModel() as IShapeModel)?.getImageFillSize?.()
            .updateBackgroundImageSize?.(props, width, height);
    };

    /**
     * 图片是否裁剪过
     */
    private hasCrop = () => {
        const { srcRect } = (this.getSelectModel() as IPictureModel).props.style;
        if (!srcRect || (srcRect.l === DEFAULT_VALUE && srcRect.r === DEFAULT_VALUE
            && srcRect.b === DEFAULT_VALUE && srcRect.t === DEFAULT_VALUE)) return false;
        return true;
    };

    private isCropBtnDisable = (): boolean => {
        const type = this.getSelectModel()?.type;
        if (type === UIModelType.GROUP || type === UIModelType.MULTIPLY) return true;
        if (type !== UIModelType.SHAPE) return false;
        const fillInfo = (this.getSelectModel() as IShapeModel).getBackgroundFill().getFillInfo();
        return !!(fillInfo?.image?.tile);
    };

    private onDropdownVisibleChange = (visible: boolean) => {
        this.updateDropdownVisible(visible, FormItemName.PICTURE_TAILOR);
    };
}
